import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Lock, Mail, LogIn, ArrowLeft } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const justReset = params.get("reset") === "1";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (e2) {
      setError(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen bg-brand-obsidian flex items-center justify-center px-4 grain relative" data-testid="admin-login-page">
      <Link to="/" data-testid="admin-login-back" className="absolute top-6 left-6 inline-flex items-center gap-2 text-sm text-white/50 hover:text-white transition-colors">
        <ArrowLeft className="h-4 w-4" /> Back to site
      </Link>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <span className="relative inline-block">
            <span className="absolute inset-0 rounded-full bg-brand-crimson/40 blur-xl" />
            <img src="/logo.png" alt="Quest K9's" className="relative h-32 w-32 object-contain mx-auto drop-shadow-[0_10px_30px_rgba(0,0,0,0.6)]" />
          </span>
          <h1 className="mt-5 font-display text-2xl font-bold text-white">Owner Login</h1>
          <p className="mt-1 text-sm text-white/50">Quest K9's control room - enquiries, invoices & content</p>
        </div>
        <form onSubmit={submit} className="rounded-lg border border-brand-line bg-brand-surface p-8 space-y-5" data-testid="admin-login-form">
          <div className="space-y-2">
            <Label htmlFor="a-email" className="text-white/80 text-sm">Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
              <Input
                id="a-email"
                type="email"
                required
                data-testid="admin-login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@questk9.co.uk"
                className="bg-brand-obsidian border-brand-line pl-10"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="a-password" className="text-white/80 text-sm">Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
              <Input
                id="a-password"
                type="password"
                required
                data-testid="admin-login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••"
                className="bg-brand-obsidian border-brand-line pl-10"
              />
            </div>
          </div>
          {justReset && !error && (
            <p className="text-sm text-emerald-400" data-testid="admin-login-reset-ok">Password updated - log in with your new password.</p>
          )}
          {error && (
            <p className="text-sm text-red-400" data-testid="admin-login-error">{error}</p>
          )}
          <div className="text-right -mt-2">
            <Link
              to={`/admin/reset-password${email ? `?email=${encodeURIComponent(email)}` : ""}`}
              data-testid="admin-login-forgot"
              className="text-xs text-white/60 hover:text-white underline underline-offset-4"
            >
              Forgot password?
            </Link>
          </div>
          <Button
            type="submit"
            disabled={busy}
            data-testid="admin-login-button"
            className="w-full bg-brand-crimson hover:bg-brand-crimhover text-white font-eyebrow uppercase tracking-[0.2em] text-xs font-bold py-6 rounded-md"
          >
            {busy ? "Checking..." : "Enter Control Room"} <LogIn className="h-4 w-4" />
          </Button>
        </form>
      </div>
    </main>
  );
}
